import { escape } from "es-toolkit";

/**
 * Builds the message a MAAC operator hands to their IT team so the DNS records
 * for an email sender domain get created.
 *
 * The operator pastes this into their own mail client rather than the platform
 * sending it, so the output has to survive a paste into Gmail's compose box and
 * still let the IT reader copy each record value byte-for-byte.
 */

export interface ItDnsEmailRecord {
  /** DNS record type, e.g. "CNAME" / "TXT". */
  type: string;
  /** Host name to create in the customer's zone. */
  host: string;
  /** Value the record must resolve to. */
  value: string;
}

/**
 * Translated strings, already interpolated by i18next. The caller passes the
 * domain through `t`'s own options, so no placeholder survives to this point --
 * i18next consumes `{{...}}` before the string ever reaches us.
 */
export interface ItDnsEmailCopy {
  intro: string;
  domainLabel: string;
  columnType: string;
  columnHost: string;
  columnValue: string;
  notesTitle: string;
  /** Bullet points rendered under the table, in order. */
  notes: ReadonlyArray<string>;
  closing: string;
}

export interface BuildItDnsEmailInput {
  records: ReadonlyArray<ItDnsEmailRecord>;
  copy: ItDnsEmailCopy;
}

export interface ItDnsEmail {
  /** Inline-styled HTML for rich paste targets. */
  html: string;
  /** Column-free plain text for everything else. */
  plain: string;
}

// es-toolkit's escape rather than a hand-rolled table: it also covers `'`,
// which today's output never needs (no single-quoted attributes, and no
// external string reaches an attribute at all) but which would become an
// injection point the moment someone interpolates into one. Keeping a security
// primitive of our own to maintain buys nothing here.
const escapeHtml = escape;

/**
 * Renders paragraph text as HTML, turning newlines in the translated copy into
 * `<br>` so the paste keeps its line breaks.
 */
const paragraphHtml = (value: string): string =>
  escapeHtml(value).replace(/\n/g, "<br>");

// Inline styles only: Gmail drops <style> blocks and class attributes.
const TABLE_STYLE =
  "border-collapse:collapse;width:100%;max-width:760px;margin:12px 0;font-size:13px";
const TH_STYLE =
  "border:1px solid #d0d5dd;padding:8px 10px;background:#f2f4f7;text-align:left;font-weight:600";
// `word-break` lets a long DKIM value wrap visually. The value itself is never
// altered -- inserting a <br> or a space inside it would hand IT a broken record.
const TD_STYLE =
  "border:1px solid #d0d5dd;padding:8px 10px;font-family:monospace;word-break:break-all";
// The record type is short and must never be split ("CNAME" broken across two
// lines reads as a typo), so it opts out of the break-all above.
const TD_TYPE_STYLE =
  "border:1px solid #d0d5dd;padding:8px 10px;font-family:monospace;white-space:nowrap";

function buildHtml({ copy, records }: BuildItDnsEmailInput): string {
  const rows = records
    .map(
      (record) =>
        `<tr>` +
        `<td style="${TD_TYPE_STYLE}">${escapeHtml(record.type)}</td>` +
        `<td style="${TD_STYLE}">${escapeHtml(record.host)}</td>` +
        `<td style="${TD_STYLE}">${escapeHtml(record.value)}</td>` +
        `</tr>`,
    )
    .join("");

  const notes = copy.notes
    .map((line) => `<li style="margin-bottom:4px">${paragraphHtml(line)}</li>`)
    .join("");

  return [
    `<div style="font-size:14px;line-height:1.6;color:#101828">`,
    `<p style="margin:0 0 12px">${paragraphHtml(copy.intro)}</p>`,
    `<p style="margin:0"><strong>${escapeHtml(copy.domainLabel)}</strong></p>`,
    `<table style="${TABLE_STYLE}">`,
    `<thead><tr>`,
    `<th style="${TH_STYLE}">${escapeHtml(copy.columnType)}</th>`,
    `<th style="${TH_STYLE}">${escapeHtml(copy.columnHost)}</th>`,
    `<th style="${TH_STYLE}">${escapeHtml(copy.columnValue)}</th>`,
    `</tr></thead>`,
    `<tbody>${rows}</tbody>`,
    `</table>`,
    `<p style="margin:16px 0 4px"><strong>${escapeHtml(
      copy.notesTitle,
    )}</strong></p>`,
    `<ul style="margin:0;padding-left:20px">${notes}</ul>`,
    `<p style="margin:16px 0 0">${paragraphHtml(copy.closing)}</p>`,
    `</div>`,
  ].join("");
}

function buildPlain({ copy, records }: BuildItDnsEmailInput): string {
  // Listed per record rather than as an aligned table: the host and value
  // columns differ in width by an order of magnitude, and CJK labels are
  // double-width, so any padding scheme breaks alignment in the reader's font.
  const recordLines = records.flatMap((record, index) => [
    `[${index + 1}] ${record.type}`,
    `    ${copy.columnHost}: ${record.host}`,
    `    ${copy.columnValue}: ${record.value}`,
    "",
  ]);

  return [
    copy.intro,
    "",
    copy.domainLabel,
    "",
    ...recordLines,
    copy.notesTitle,
    ...copy.notes.map((line) => `- ${line}`),
    "",
    copy.closing,
  ].join("\n");
}

export function buildItDnsEmail(input: BuildItDnsEmailInput): ItDnsEmail {
  return {
    html: buildHtml(input),
    plain: buildPlain(input),
  };
}
